export type CostType = 'RECEIPT' | 'TRAVEL';

export interface CostWarning {
  code?: string;
  message?: string;
}

export interface CostItem {
  name?: string;
  data?: string;
  price: number | null;
}

export interface CostProject {
  id: string;
  number?: string | null;
  title?: string | null;
  contact?: { name?: string | null; company?: string | null } | null;
}

export interface CostVehicle {
  id: string;
  name?: string | null;
  year?: number | string | null;
  make?: string | null;
  model?: string | null;
  licensePlate?: string | null;
  energyType?: string | null;
  consumptionPer100Km?: number | string | null;
  electricityPricePerKwh?: number | string | null;
  electricityPriceCurrency?: string | null;
  electricityPriceCountry?: string | null;
  fuelCountryCode?: string | null;
  isDefault?: boolean;
  odometerUnit?: string | null;
  catalogueSource?: string | null;
  catalogueSourceId?: string | null;
  catalogueSourceUrl?: string | null;
  details?: string | null;
}

export interface CostRecord {
  origin?: 'LEGACY' | 'SUPPLIER_INVOICE'; paymentState?: 'UNKNOWN' | 'UNPAID' | 'PAID'; sourceReviewRevision?: number | null;
  id: string;
  type: CostType | string;
  description?: string | null;
  status?: string | null;
  accounting?: {
    status?: string | null;
    provider?: 'BEXIO' | 'ABACUS' | null;
    mode?: 'PAID_FIBU' | 'SUPPLIER_INVOICE' | 'DOCUMENT_ONLY' | null;
    lastSyncAt?: string | null;
    lastSyncError?: string | null;
    externalId?: string | null;
    externalStatus?: string | null;
    attempts?: number | null;
    snapshot?: Record<string, unknown> | null;
  } | null;
  bexio?: {
    status?: string | null;
    lastSyncAt?: string | null;
    lastSyncError?: string | null;
    externalId?: string | null;
    externalStatus?: string | null;
    attempts?: number | null;
    accountingSnapshot?: Record<string, unknown> | null;
  } | null;
  fxSnapshot?: {
    baseCurrencyCode?: string | null;
    exchangeRate?: number | string | null;
    baseCurrencyAmount?: number | string | null;
    rateSource?: string | null;
    rateCapturedAt?: string | null;
    isForeignCurrency?: boolean;
    missingFields?: string[] | null;
  } | null;
  project?: CostProject | null;
  createdAt?: string | null;
  occurredAt?: string | null;
  createdBy?: { id?: string; name?: string | null; email?: string | null } | null;
  evidence?: Array<{ id?: string; kind?: string; url?: string; mimeType?: string; sizeBytes?: number }> | null;
  receiptAccounting?: {
    requiredFields?: string[] | null;
    bexioRequiredFields?: string[] | null;
    missingFields?: string[] | null;
  } | null;
  category?: string | null;
  amount?: number | string | null;
  currency?: string | null;
  displayAmount?: number | string | null;
  displayCurrency?: string | null;
  displayConversion?: {
    rate?: number | string | null;
    rateDate?: string | null;
    source?: string | null;
    stale?: boolean;
  } | null;
  receipt?: {
    merchantName?: string | null;
    invoiceNumber?: string | null;
    vatAmount?: number | null;
    vatRate?: number | null;
    paymentMethod?: string | null;
    details?: Array<{ label: string; value: string }> | null;
    items?: CostItem[] | null;
    warnings?: CostWarning[] | null;
    capturedAt?: string | null;
  } | null;
  travel?: {
    workflowVersion?: number; state?: string; revision?: number; sessionId?: string; recordingHealth?: string; reportingTimezone?: string; drivingSeconds?: number; dwellSeconds?: number;
    vehicleId?: string | null;
    driver?: { id?: string; name?: string | null; email?: string | null } | null;
    timeEntryId?: string | null;
    startedAt?: string | null;
    completedAt?: string | null;
    startOdometer?: number | null;
    endOdometer?: number | null;
    odometerUnit?: string | null;
    distanceKm?: number | null;
    vehicleName?: string | null;
    energyType?: string | null;
    consumptionPer100Km?: number | null;
    energyPricePerUnit?: number | null;
    energyPriceCurrency?: string | null;
    energyPriceCountry?: string | null;
    energyPriceSource?: string | null;
    energyPriceSourceUrl?: string | null;
    energyPriceUpdatedAt?: string | null;
    energyQuantity?: number | null;
    warnings?: CostWarning[] | null;
  } | null;
}

export interface ReceiptAnalysis {
  capturedAt?: string | null;
  merchantName?: string | null;
  category?: string | null;
  amount?: number | string | null;
  currency?: string | null;
  invoiceNumber?: string | null;
  occurredAt?: string | null;
  vatAmount?: number | string | null;
  vatRate?: number | string | null;
  paymentMethod?: string | null;
  missingFields?: string[] | null;
  requiredFields?: string[] | null;
  bexioRequiredFields?: string[] | null;
  details?: Array<{ label?: string; value?: string }> | Record<string, unknown> | null;
  items?: CostItem[] | null;
  warnings?: CostWarning[] | null;
}

export interface MileageAnalysis {
  capturedAt?: string | null;
  mileage?: number | string | null;
  unit?: string | null;
  warnings?: CostWarning[] | null;
}

type UnknownRecord = Record<string, unknown>;

export function asRecord(value: unknown): UnknownRecord {
  return value && typeof value === 'object' ? value as UnknownRecord : {};
}

export function nested(cost: CostRecord, key: 'receipt' | 'travel'): UnknownRecord {
  return asRecord(cost[key]);
}

function firstValue(...values: unknown[]): unknown {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

export function textValue(...values: unknown[]): string {
  const value = firstValue(...values);
  return value === undefined ? '' : String(value);
}

export function numberValue(...values: unknown[]): number | null {
  const value = firstValue(...values);
  if (value === undefined) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

export function isReceipt(cost: CostRecord): boolean {
  return String(cost.type).toUpperCase() === 'RECEIPT';
}

export function costDate(cost: CostRecord): string {
  return textValue(cost.travel?.completedAt, cost.occurredAt, cost.createdAt);
}

export function costMerchant(cost: CostRecord): string {
  const receipt = nested(cost, 'receipt');
  return textValue(receipt.merchantName);
}

export function costAmount(cost: CostRecord): number | null {
  return numberValue(cost.amount);
}

export function costCurrency(cost: CostRecord): string {
  return textValue(cost.currency) || 'CHF';
}

export function costDisplayAmount(cost: CostRecord): number | null {
  return numberValue(cost.displayAmount);
}

export function costDisplayCurrency(cost: CostRecord): string {
  return textValue(cost.displayCurrency) || costCurrency(cost);
}

export function costCategory(cost: CostRecord): string {
  return textValue(cost.category) || 'other';
}

export function costDistance(cost: CostRecord): number | null {
  return numberValue(cost.travel?.distanceKm);
}

export function costDuration(cost: CostRecord): number | null {
  const travel = nested(cost, 'travel');
  const startedAt = textValue(travel.startedAt);
  const completedAt = textValue(travel.completedAt);
  if (!startedAt || !completedAt) return null;
  const elapsed = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  return Number.isFinite(elapsed) && elapsed >= 0 ? Math.round(elapsed / 60_000) : null;
}

export function costMileage(cost: CostRecord, phase: 'start' | 'end'): number | null {
  const travel = nested(cost, 'travel');
  return numberValue(phase === 'start' ? travel.startOdometer : travel.endOdometer);
}

export function costWarnings(cost: CostRecord): CostWarning[] {
  const receiptWarnings = nested(cost, 'receipt').warnings;
  const travelWarnings = nested(cost, 'travel').warnings;
  const source = Array.isArray(receiptWarnings) ? receiptWarnings : Array.isArray(travelWarnings) ? travelWarnings : [];
  return source.map((warning) => typeof warning === 'string' ? { code: warning } : warning as CostWarning);
}

export function syncState(cost: CostRecord): string {
  return textValue(cost.accounting?.status, cost.bexio?.status) || 'NOT_SYNCED';
}

export function reviewState(cost: CostRecord): string {
  return textValue(cost.status) || 'PENDING';
}

export function vehicleLabel(vehicle: CostVehicle | null | undefined): string {
  if (!vehicle) return '';
  return textValue(vehicle.name)
    || [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')
    || textValue(vehicle.licensePlate);
}

export function projectLabel(project: CostProject | null | undefined): string {
  if (!project) return '';
  return [textValue(project.number), textValue(project.title), textValue(project.contact?.name, project.contact?.company)]
    .filter(Boolean).join(' · ');
}
